import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  HelpOutline as HelpIcon,
  AccountTree as ChainIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WebIcon from "@mui/icons-material/Web";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { UseCasesModal } from "../components/UseCasesModal";
import { BuildHero } from "../components/dashboard/BuildHero";
import { CTATour } from "../components/CTATour";
import { DashboardOverview } from "../components/dashboard/DashboardOverview";
import { useViewMode } from "../contexts/ViewModeContext";

export default function Dashboard() {
  const { hasActiveSubscription } = useAuth();
  const { setViewMode } = useViewMode();
  const navigate = useNavigate();

  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const [showTourNotification, setShowTourNotification] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenCTATour');
    if (!hasSeenTour) {
      setShowTourNotification(true);
    }
  }, []);

  const openChains = () => {
    setViewMode('chaining' as any);
    navigate('/dashboard');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, position: 'relative' }}>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => {
            setTourOpen(true);
            setShowTourNotification(false);
            localStorage.setItem('hasSeenCTATour', 'true');
          }}
        >
          Start Tour
        </Button>
        <Tooltip title="Restart Platform Tour">
          <IconButton 
            onClick={() => {
              setTourOpen(true);
              setShowTourNotification(false);
              localStorage.setItem('hasSeenCTATour', 'true');
            }}
            size="small"
            sx={{ ml: 1 }}
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
        {showTourNotification && (
          <Paper
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                p: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 2,
                boxShadow: 3,
                zIndex: 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.05)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  right: 20,
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid',
                  borderTopColor: 'primary.main',
                }
              }}
              onClick={() => {
                setTourOpen(true);
                setShowTourNotification(false);
                localStorage.setItem('hasSeenCTATour', 'true');
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                New here? Take a quick tour!
              </Typography>
            </Paper>
          )}
      </Box>

      <BuildHero hasActiveSubscription={hasActiveSubscription} onShowUseCases={() => setUseCasesOpen(true)} />

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Or jump straight in
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            title: 'Chat with any AI',
            body: 'Talk to any model in Playground — no app to generate.',
            icon: <ChatBubbleOutlineIcon sx={{ fontSize: 32 }} />,
            onClick: () => navigate('/dashboard/playground?mode=chat'),
          },
          {
            title: 'Make an app',
            body: 'Describe a product. Preview it live, then wrap your chains.',
            icon: <WebIcon sx={{ fontSize: 32 }} />,
            onClick: () => navigate('/dashboard/playground?mode=app_builder&new=1'),
          },
          {
            title: 'Make a function',
            body: 'An AI endpoint you call from code. Describe it, or set instructions, models and output yourself.',
            icon: <FunctionsIcon sx={{ fontSize: 32 }} />,
            onClick: () => navigate('/dashboard/ai-functions'),
          },
          {
            title: 'Make a chain',
            body: 'Wire functions into a workflow you can run or turn into an app.',
            icon: <ChainIcon sx={{ fontSize: 32 }} />,
            onClick: openChains,
          },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Paper
              elevation={0}
              onClick={item.onClick}
              sx={{
                p: 2.25,
                height: '100%',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Box sx={{ color: 'primary.main', mt: 0.25 }}>{item.icon}</Box>
              <Box>
                <Typography sx={{ fontWeight: 800, mb: 0.4 }}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {item.body}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <DashboardOverview onOpenChains={openChains} />

      <UseCasesModal open={useCasesOpen} onClose={() => setUseCasesOpen(false)} />

      <CTATour 
        open={tourOpen} 
        onClose={() => setTourOpen(false)}
        onNavigate={(path) => {
          navigate(path);
        }}
      />
    </Box>
  );
}
